import React from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
// Enable OAuth listener for Amplify v6 to automatically process OAuth callbacks
// This must be imported at the app entry point
import 'aws-amplify/auth/enable-oauth-listener';

import Layout from './Layout';
import Landing from './Landing';
import JobForm from './jobs/JobForm';
import ArchivedJobs from './jobs/archived/ArchivedJobs';
import ProtectedRoute from './ProtectedRoute';
import AuthCallback from './account/AuthCallback';
import AccountSignup from './account/AccountSignup';
import Billing from './billing/Billing';
import PaymentMethodNew from './billing/method/PaymentMethodNew';
import PaymentHistory from './billing/PaymentHistory';
import AccountSettings from './account/AccountSettings';
import Dashboard from './Dashboard';
import JobDashboard from './jobs/Dashboard';
import JobDetails from './jobs/JobDetails';
import JobExecutionLogs from './jobs/ExecutionLogs';
import ResourceDashboard from './resources/ResourceDashboard';
import RegionDashboard from './resources/RegionDashboard';
import ProviderForm from './resources/ProviderForm';
import RegionForm from './resources/RegionForm';
import NodeForm from './resources/RegionDashboard/NodeForm';
import Node from './resources/RegionDashboard/Nodes/Node';
import DatastoreForm from './resources/RegionDashboard/DatastoreForm';
import DataConnectorForm from './resources/RegionDashboard/DataConnectorForm';
import ServiceForm from './resources/RegionDashboard/ServiceForm';
import DeviceConfigForm from './resources/RegionDashboard/Nodes/Devices/DeviceConfigForm';
import CollectionsDashboard from './collections/CollectionDashboard';
import CollectionForm from './collections/CollectionForm';
import Collection from './collections/Collection';
import CollectionExecutionLogs from './collections/ExecutionLogs';
import ProjectDashboard from './projects/ProjectDashboard';
import Project from './projects/Project';
import ProjectForm from './projects/ProjectForm';
import ProjectMemberForm from '@/components/projects/Project/ProjectMembers/ProjectMemberForm';
import ProjectAuthorizerForm from '@/components/projects/Project/ProjectAuthorizers/ProjectAuthorizerForm';
import {
  LlmConfigsProvider,
  ProjectAuthorizersProvider,
  useUser,
} from '@/providers';

function JobProviders({ children }) {
  return <LlmConfigsProvider>{children}</LlmConfigsProvider>;
}

function JobFormProviders({ children }) {
  const user = useUser();
  return (
    <LlmConfigsProvider>
      <ProjectAuthorizersProvider project_id={user?.selected_project}>
        {children}
      </ProjectAuthorizersProvider>
    </LlmConfigsProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      {
        path: '/',
        element: <Landing />,
      },
      {
        path: '/logout',
        element: <Landing />,
      },
      {
        path: '/auth/callback',
        element: <AuthCallback />,
      },
      {
        path: '/account/signup',
        element: <AccountSignup />,
      },
      {
        path: '/archive/jobs',
        element: (
          <ProtectedRoute>
            <JobProviders>
              <ArchivedJobs />
            </JobProviders>
          </ProtectedRoute>
        ),
      },
      {
        path: '/jobs/:type/edit',
        element: (
          <ProtectedRoute>
            <JobFormProviders>
              <JobForm />
            </JobFormProviders>
          </ProtectedRoute>
        ),
      },
      {
        path: '/jobs/:type/details/:job_uuid',
        element: (
          <ProtectedRoute>
            <JobProviders>
              <JobDetails />
            </JobProviders>
          </ProtectedRoute>
        ),
      },
      {
        path: '/jobs/:type/logs/:job_uuid',
        element: (
          <ProtectedRoute>
            <JobProviders>
              <JobExecutionLogs />
            </JobProviders>
          </ProtectedRoute>
        ),
      },
      {
        path: '/jobs/:type',
        element: (
          <ProtectedRoute>
            <JobProviders>
              <JobDashboard />
            </JobProviders>
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dataset',
        element: (
          <ProtectedRoute>
            <CollectionsDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dataset/edit',
        element: (
          <ProtectedRoute>
            <CollectionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dataset/logs/:id',
        element: (
          <ProtectedRoute>
            <CollectionExecutionLogs />
          </ProtectedRoute>
        ),
      },
      {
        path: '/dataset/details/:id',
        element: (
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        ),
      },
      {
        path: '/model',
        element: (
          <ProtectedRoute>
            <CollectionsDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/model/edit',
        element: (
          <ProtectedRoute>
            <CollectionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/model/logs/:id',
        element: (
          <ProtectedRoute>
            <CollectionExecutionLogs />
          </ProtectedRoute>
        ),
      },
      {
        path: '/model/details/:id',
        element: (
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        ),
      },
      {
        path: '/checkpoint',
        element: (
          <ProtectedRoute>
            <CollectionsDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/checkpoint/edit',
        element: (
          <ProtectedRoute>
            <CollectionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/checkpoint/details/:id',
        element: (
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        ),
      },
      {
        path: '/checkpoint/logs/:id',
        element: (
          <ProtectedRoute>
            <CollectionExecutionLogs />
          </ProtectedRoute>
        ),
      },
      {
        path: '/volume',
        element: (
          <ProtectedRoute>
            <CollectionsDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/volume/edit',
        element: (
          <ProtectedRoute>
            <CollectionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/volume/details/:id',
        element: (
          <ProtectedRoute>
            <Collection />
          </ProtectedRoute>
        ),
      },
      {
        path: '/volume/logs/:id',
        element: (
          <ProtectedRoute>
            <CollectionExecutionLogs />
          </ProtectedRoute>
        ),
      },
      {
        path: '/projects',
        element: (
          <ProtectedRoute>
            <ProjectDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/edit',
        element: (
          <ProtectedRoute>
            <ProjectForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/details/:id',
        element: (
          <ProtectedRoute>
            <ProjectAuthorizersProvider>
              <Outlet />
            </ProjectAuthorizersProvider>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Project />,
          },
          {
            path: 'member/edit',
            element: <ProjectMemberForm />,
          },
          {
            path: 'authorizer/edit',
            element: <ProjectAuthorizerForm />,
          },
        ],
      },
      {
        path: '/account/billing',
        element: (
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        ),
      },
      {
        path: '/account/billing/history',
        element: (
          <ProtectedRoute>
            <PaymentHistory />
          </ProtectedRoute>
        ),
      },
      {
        path: '/account/billing/method/new',
        element: (
          <ProtectedRoute>
            <PaymentMethodNew />
          </ProtectedRoute>
        ),
      },
      {
        path: '/account/settings',
        element: (
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources',
        element: (
          <ProtectedRoute>
            <ResourceDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/new',
        element: (
          <ProtectedRoute>
            <ProviderForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/edit',
        element: (
          <ProtectedRoute>
            <ProviderForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/new',
        element: (
          <ProtectedRoute>
            <RegionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/edit',
        element: (
          <ProtectedRoute>
            <RegionForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/dashboard',
        element: (
          <ProtectedRoute>
            <RegionDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/node/new',
        element: (
          <ProtectedRoute>
            <NodeForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/node/new',
        element: (
          <ProtectedRoute>
            <NodeForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/node/:rig_uuid/edit',
        element: (
          <ProtectedRoute>
            <NodeForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/node/:rig_uuid/details',
        element: (
          <ProtectedRoute>
            <Node />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/device/config',
        element: (
          <ProtectedRoute>
            <DeviceConfigForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/datastore/edit',
        element: (
          <ProtectedRoute>
            <DatastoreForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/data_connector/edit',
        element: (
          <ProtectedRoute>
            <DataConnectorForm />
          </ProtectedRoute>
        ),
      },
      {
        path: '/resources/provider/:provider_uuid/region/:region_uuid/service/edit',
        element: (
          <ProtectedRoute>
            <ServiceForm />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
